import React from "react";

import moment from "moment";

import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import CircularProgress from "@material-ui/core/CircularProgress";
import Alert from "@material-ui/lab/Alert";
import { makeStyles } from "@material-ui/core/styles";

import CompareArrowsIcon from "@material-ui/icons/CompareArrows";

const CELL_PADDING_X = 1.5;
const SECTION_ACCENT_WIDTH = 3;

const INCREASE_COLOR = "#19a86d";
const DECREASE_COLOR = "#de4141";
const CHANGED_COLOR = "#dfad1d";

const useStyles = makeStyles((theme) => ({
  dialogTitleRoot: {
    paddingBottom: theme.spacing(1.5),
  },
  dialogTitleInner: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(2),
  },
  titleLeft: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  totalBadge: {
    textAlign: "right",
  },
  table: {
    tableLayout: "fixed",
  },
  headCell: {
    fontWeight: 700,
    width: "33.33%",
  },
  // Every cell shares the same horizontal padding so the section titles,
  // labels and values line up in the same three columns.
  cell: {
    fontSize: "0.85rem",
    padding: theme.spacing(0.75, CELL_PADDING_X),
  },
  spacerCell: {
    height: theme.spacing(2.5),
    padding: 0,
    borderBottom: "none",
  },
  sectionCell: {
    padding: theme.spacing(1, CELL_PADDING_X),
    paddingLeft: `calc(${theme.spacing(
      CELL_PADDING_X
    )}px - ${SECTION_ACCENT_WIDTH}px)`,
    borderLeft: `${SECTION_ACCENT_WIDTH}px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.action.selected,
    color: theme.palette.primary.light,
    fontSize: "0.75rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  },
  boldCell: {
    fontWeight: 700,
    borderTop: `2px solid ${theme.palette.divider}`,
  },
  footnote: {
    fontSize: "0.7rem",
    color: theme.palette.text.disabled,
    marginTop: theme.spacing(1),
  },
  alert: {
    marginTop: theme.spacing(2),
  },
}));

const fmt = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const toNumber = (value) => Number(value) || 0;
const toCents = (value) => Math.round(toNumber(value) * 100);
const formatCents = (cents) => fmt.format(cents / 100);

const rateDateKey = (date) => moment(date).format("YYYY-MM-DD");
const formatDate = (date) => moment(rateDateKey(date)).format("ll");

const nightsBetween = (checkIn, checkOut) =>
  moment(rateDateKey(checkOut)).diff(moment(rateDateKey(checkIn)), "days");

const changeColor = (current, next) =>
  next > current ? INCREASE_COLOR : next < current ? DECREASE_COLOR : undefined;

const formatDifference = (cents) =>
  `${cents > 0 ? "+" : cents < 0 ? "−" : ""}${fmt.format(
    Math.abs(cents) / 100
  )}`;

const rateTotalCents = (rate) =>
  rate
    ? Math.round(toNumber(rate.price) * (1 + toNumber(rate.tax) / 100) * 100)
    : 0;

const formatRate = (rate) =>
  rate
    ? `${fmt.format(toNumber(rate.price))} + ${toNumber(rate.tax).toFixed(
        2
      )}% tax`
    : "—";

const toAmountCents = (amounts) => ({
  ratesSubtotalCents: toCents(amounts.rates_subtotal),
  ratesTaxCents: toCents(amounts.rates_tax),
  addonsSubtotalCents: toCents(amounts.addons_subtotal),
  addonsTaxCents: toCents(amounts.addons_tax),
  resortFeeCents: toCents(amounts.resort_fee_total),
  totalCents: toCents(amounts.total),
  discountCents: toCents(amounts.discount_amount),
  postDiscountTotalCents: toCents(amounts.post_discount_total),
});

const buildNightlyRows = (currentRates, nextRates) => {
  const currentByDate = new Map(
    currentRates.map((rate) => [rateDateKey(rate.date), rate])
  );
  const nextByDate = new Map(
    nextRates.map((rate) => [rateDateKey(rate.date), rate])
  );
  const dates = Array.from(
    new Set([...currentByDate.keys(), ...nextByDate.keys()])
  ).sort();

  return dates.map((date) => ({
    date,
    current: currentByDate.get(date),
    next: nextByDate.get(date),
  }));
};

const formatProduct = (product) =>
  product
    ? `${toNumber(product.quantity)} × ${fmt.format(
        toNumber(product.price)
      )} + ${toNumber(product.tax).toFixed(2)}% tax · ${product.type}`
    : "—";

const productChanged = (a, b) =>
  [
    "name",
    "price",
    "tax",
    "quantity",
    "type",
    "age_restriction",
    "product_type",
  ].some((field) => String(a[field]) !== String(b[field]));

// Pairs current and new products: by product_id when both have one, then by
// name for rows added in the editor.
const buildProductRows = (currentProducts = [], nextProducts = []) => {
  const remaining = [...currentProducts];
  const rows = nextProducts.map((next) => {
    let index = remaining.findIndex(
      (current) =>
        next.product_id !== undefined && current.product_id === next.product_id
    );
    if (index === -1)
      index = remaining.findIndex((current) => current.name === next.name);

    const current = index === -1 ? null : remaining.splice(index, 1)[0];
    return { current, next };
  });

  return [...rows, ...remaining.map((current) => ({ current, next: null }))];
};

const SectionRow = ({ label, first }) => {
  const classes = useStyles();

  return (
    <>
      {!first && (
        <TableRow>
          <TableCell colSpan={3} className={classes.spacerCell} />
        </TableRow>
      )}
      <TableRow>
        <TableCell colSpan={3} className={classes.sectionCell}>
          {label}
        </TableCell>
      </TableRow>
    </>
  );
};

const CompareRow = ({ label, current, next, nextColor, bold }) => {
  const classes = useStyles();
  const cellClass = bold ? `${classes.cell} ${classes.boldCell}` : classes.cell;

  return (
    <TableRow>
      <TableCell className={cellClass}>{label}</TableCell>
      <TableCell className={cellClass}>{current}</TableCell>
      <TableCell
        className={cellClass}
        style={nextColor ? { color: nextColor, fontWeight: 600 } : undefined}
      >
        {next}
      </TableCell>
    </TableRow>
  );
};

const RatesChangePreviewDialog = ({
  open,
  onClose,
  onConfirm,
  isSubmitting,
  submitError,
  // Booking changes: dates, hotel, room, coupon and nightly rates. Optional.
  current,
  next,
  // Product changes. Optional.
  currentProducts,
  nextProducts,
  roomQty,
  amounts,
  // Calculated by the API's preview endpoint from the pending changes
  nextAmounts,
}) => {
  const classes = useStyles();

  if (!open) return null;

  const qty = Number(roomQty) || 1;
  const hasBooking = Boolean(current && next);
  const hasProducts = Boolean(currentProducts && nextProducts);
  const currentNights = hasBooking
    ? nightsBetween(current.check_in, current.check_out)
    : 0;
  const nextNights = hasBooking
    ? nightsBetween(next.check_in, next.check_out)
    : 0;

  const before = toAmountCents(amounts);
  const after = toAmountCents(nextAmounts);

  const netPaidCents =
    toCents(amounts.totalPaid) - toCents(amounts.totalRefunds);
  before.netPaidCents = netPaidCents;
  after.netPaidCents = netPaidCents;
  before.remainingCents = Math.max(
    before.postDiscountTotalCents - netPaidCents,
    0
  );
  after.remainingCents = Math.max(
    after.postDiscountTotalCents - netPaidCents,
    0
  );
  const overpaidCents = netPaidCents - after.postDiscountTotalCents;
  const totalDifferenceCents =
    after.postDiscountTotalCents - before.postDiscountTotalCents;

  const stayRows = !hasBooking
    ? []
    : [
        { label: "Hotel", current: current.hotelName, next: next.hotelName },
        { label: "Room", current: current.roomName, next: next.roomName },
        {
          label: "Check In",
          current: formatDate(current.check_in),
          next: formatDate(next.check_in),
        },
        {
          label: "Check Out",
          current: formatDate(current.check_out),
          next: formatDate(next.check_out),
        },
        { label: "Coupon", current: current.couponName, next: next.couponName },
      ];

  const amountRows = [
    {
      label: `Rates Subtotal${qty > 1 ? ` (× ${qty} rooms)` : ""}`,
      key: "ratesSubtotalCents",
    },
    { label: "Rates Tax", key: "ratesTaxCents" },
    { label: "Addons Subtotal", key: "addonsSubtotalCents" },
    { label: "Addons Tax", key: "addonsTaxCents" },
    { label: "Resort Fee", key: "resortFeeCents" },
    { label: "Total", key: "totalCents", bold: true },
    ...(before.discountCents > 0 || after.discountCents > 0
      ? [
          { label: "Discount", key: "discountCents", negative: true },
          {
            label: "Total After Discount",
            key: "postDiscountTotalCents",
            bold: true,
          },
        ]
      : []),
    { label: "Net Amount Paid", key: "netPaidCents" },
    { label: "Amount Remaining", key: "remainingCents", bold: true },
  ];

  const nightlyRows = hasBooking
    ? buildNightlyRows(current.rates, next.rates)
    : [];
  const productRows = hasProducts
    ? buildProductRows(currentProducts, nextProducts)
    : [];
  const hasChanges =
    totalDifferenceCents !== 0 ||
    productRows.some(
      (row) =>
        !row.current || !row.next || productChanged(row.current, row.next)
    ) ||
    stayRows.some((row) => row.current !== row.next) ||
    nightlyRows.some(
      (row) =>
        !row.current ||
        !row.next ||
        toNumber(row.current.price) !== toNumber(row.next.price) ||
        toNumber(row.current.tax) !== toNumber(row.next.tax)
    );

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle
        disableTypography
        classes={{ root: classes.dialogTitleRoot }}
      >
        <div className={classes.dialogTitleInner}>
          <div className={classes.titleLeft}>
            <CompareArrowsIcon color="primary" />
            <Typography variant="h6">Preview New Pricing</Typography>
          </div>
          <div className={classes.totalBadge}>
            <Typography variant="caption" color="textSecondary" display="block">
              Total Change
            </Typography>
            <Typography
              variant="h5"
              style={{
                color: changeColor(0, totalDifferenceCents),
                fontWeight: 700,
                lineHeight: 1.2,
              }}
            >
              {formatDifference(totalDifferenceCents)}
            </Typography>
          </div>
        </div>
      </DialogTitle>

      <DialogContent dividers>
        <Table size="small" stickyHeader className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell className={`${classes.cell} ${classes.headCell}`} />
              <TableCell className={`${classes.cell} ${classes.headCell}`}>
                Current
              </TableCell>
              <TableCell className={`${classes.cell} ${classes.headCell}`}>
                New
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {hasBooking && <SectionRow label="Stay" first />}
            {stayRows.map((row) => (
              <CompareRow
                key={row.label}
                label={row.label}
                current={row.current || "—"}
                next={row.next || "—"}
                nextColor={row.current !== row.next ? CHANGED_COLOR : undefined}
              />
            ))}
            {hasBooking && (
              <>
                <CompareRow
                  label="Nights"
                  current={currentNights}
                  next={nextNights}
                  nextColor={changeColor(currentNights, nextNights)}
                />
                <SectionRow
                  label={`Nightly Rates${qty > 1 ? " (per room)" : ""}`}
                />
              </>
            )}
            {nightlyRows.map((row) => (
              <CompareRow
                key={row.date}
                label={formatDate(row.date)}
                current={formatRate(row.current)}
                next={row.next ? formatRate(row.next) : "Removed"}
                nextColor={changeColor(
                  rateTotalCents(row.current),
                  rateTotalCents(row.next)
                )}
              />
            ))}

            {hasProducts && <SectionRow label="Products" first />}
            {productRows.map(({ current: before, next: after }, index) => (
              <CompareRow
                key={`product-${index}`}
                label={(after || before).name}
                current={formatProduct(before)}
                next={after ? formatProduct(after) : "Removed"}
                nextColor={
                  !before
                    ? INCREASE_COLOR
                    : !after
                    ? DECREASE_COLOR
                    : productChanged(before, after)
                    ? CHANGED_COLOR
                    : undefined
                }
              />
            ))}

            <SectionRow label="Order Amounts" />
            {amountRows.map(({ label, key, bold, negative }) => {
              const sign = negative ? -1 : 1;

              return (
                <CompareRow
                  key={key}
                  label={label}
                  bold={bold}
                  current={formatCents(before[key] * sign)}
                  next={formatCents(after[key] * sign)}
                  nextColor={changeColor(before[key] * sign, after[key] * sign)}
                />
              );
            })}
          </TableBody>
        </Table>

        {overpaidCents > 0 && (
          <Alert severity="warning" className={classes.alert}>
            The customer has already paid {formatCents(overpaidCents)} more than
            the new total. A refund may be needed.
          </Alert>
        )}

        {!hasChanges && (
          <Alert severity="info" className={classes.alert}>
            No pricing changes detected.
          </Alert>
        )}

        {submitError && (
          <Alert severity="error" className={classes.alert}>
            There was an error saving the changes. Please try again.
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Back to Edit
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={onConfirm}
          disabled={isSubmitting}
          startIcon={
            isSubmitting ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {isSubmitting ? "Saving…" : "Submit Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RatesChangePreviewDialog;
